import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { POSTS } from "@/lib/posts";

export default function Blog() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Property Insights — Declan Bishop Property Consultant",
    url: "https://dbpropertyconsultant.co.uk/blog",
    blogPost: POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      url: `https://dbpropertyconsultant.co.uk/blog/${p.slug}`,
    })),
  };

  return (
    <SiteLayout
      pageTitle="Property Insights & Guides"
      pageDescription="Plain-English property guides from an independent Lincolnshire surveyor — snagging, EPCs, damp, surveys and advice for homeowners, landlords and buyers."
      pagePath="/blog"
    >
      <JsonLd id="blog-schema" data={schema} />
      <section data-testid="blog-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="overline" data-testid="blog-overline">Property insights</div>
          <h1
            data-testid="blog-headline"
            className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            Notes from the field — <span className="italic font-light text-brand-champagne">written in plain English.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
            Practical guidance on surveys, snagging, damp and energy performance for
            Lincolnshire homeowners, landlords and buyers.
          </p>
        </div>
      </section>

      <section data-testid="blog-list" className="bg-white">
        <div className="container-narrow py-20 md:py-28">
          <div className="space-y-px bg-brand-navy/10 border border-brand-navy/10">
            {POSTS.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block bg-white p-8 md:p-12 hover:bg-brand-cream transition-colors"
              >
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] tracking-[0.22em] uppercase font-semibold">
                  <span className="text-brand-champagne">{p.category}</span>
                  <span className="text-brand-navy/45">{p.dateLabel}</span>
                  <span className="inline-flex items-center gap-1.5 text-brand-navy/45">
                    <Clock size={12} />
                    {p.readTime}
                  </span>
                </div>
                <h2 className="mt-5 font-serif text-2xl md:text-4xl text-brand-navy leading-tight tracking-tight max-w-3xl">
                  {p.title}
                </h2>
                <p className="mt-4 max-w-2xl text-sm md:text-base text-brand-navy/65 leading-relaxed">
                  {p.excerpt}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link">
                  Read article
                  <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-10 text-sm text-brand-navy/55">
            More guides are on the way — covering EPCs, damp diagnosis and buying with confidence in Lincolnshire.
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
